import { nextTick } from 'vue';
import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  type RouteRecordRaw,
} from 'vue-router';
import { type FeatureFlag, featureEnabled, features, type Platform } from '@/features';
import useAuthStore from '@/store/modules/auth';
import useEnvStore from '@/store/modules/env';

const redirectWithParams =
  (name: string) =>
  ({ params }: { params: Record<string, string | string[]> }) => ({
    name,
    params,
  });

const DEFAULT_TITLE: string = 'SparkLayer Dashboard';

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'home',
    component: () => import('../views/Redirect.vue'),
    meta: { auth: true },
  },
  {
    path: '/:siteId/:siteEnv/forms',
    name: 'forms-app',
    meta: { auth: true, name: 'Forms' },
    component: () => import('../views/FormsApp.vue'),
  },
  {
    // Catch-all for forms app internal TanStack routes.
    path: '/:siteId/:siteEnv/forms/:pathMatch(.*)*',
    name: 'forms-app-catchall',
    meta: { auth: true, name: 'Forms' },
    component: () => import('../views/FormsApp.vue'),
  },
  {
    path: '/getting-ready',
    name: 'Getting Ready',
    component: () => import('../views/GettingReady.vue'),
    meta: { auth: true },
  },
  {
    path: '/:siteId',
    name: 'team-redirect',
    component: () => import('../views/Redirect.vue'),
    meta: { auth: true, redirectName: 'home' },
  },
  {
    path: '/:siteId/partners',
    name: 'partners',
    // @ts-expect-error no types
    component: () => import('../views/Partners/Main.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'partners-redirect',
        redirect: redirectWithParams('partner-overview'),
      },
      {
        path: 'overview',
        name: 'partner-overview',
        // @ts-expect-error no types
        component: () => import('../views/Partners/Overview.vue'),
        meta: {
          auth: true,
          name: 'Partner Dashboard',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv',
    name: 'team-overview',
    component: () => import('../views/HomeRouter.vue'),
    meta: { auth: true },
  },
  {
    path: '/:siteId/:siteEnv/activity',
    name: 'activity',
    component: () => import('../views/SalesActivity/ActivityV2.vue'),
    meta: {
      auth: true,
      name: 'Activity',
    },
  },
  {
    path: '/:siteId/:siteEnv/configuration',
    name: 'configuration',
    // @ts-expect-error no types
    component: () => import('../views/Settings/Settings.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'config-redirect',
        redirect: redirectWithParams('config-onboarding'),
      },
      {
        path: 'onboarding',
        name: 'config-onboarding',
        // @ts-expect-error no types
        component: () => import('../views/Settings/SettingsSetup.vue'),
        meta: {
          auth: true,
          name: 'Setup - Settings',
        },
      },
      {
        path: 'integrations',
        name: 'partner-integrations',
        component: () => import('../views/Settings/SettingsIntegrations.vue'),
        children: [
          {
            path: '',
            name: 'partner-integrations-redirect',
            redirect: redirectWithParams('integrations-platform'),
          },
          {
            path: 'platform',
            name: 'integrations-platform-redirect',
            redirect: redirectWithParams('integrations-platform'),
          },
          // todo: tech debt is to move all these the oauth redirects
          // FOR NOT DO MOVE AS IT'LL BREAK THE INTERGATIONs
          {
            path: 'oauth-brightpearl',
            name: 'config-int-brightpearl',
            component: () => import('../views/Settings/OAuth/Brightpearl.vue'),
            meta: {
              auth: true,
              name: 'Brightpearl - Integrations (Partner Integrations) - Settings',
            },
          },
          {
            path: 'oauth-katana',
            name: 'config-int-katana',
            component: () => import('../views/Settings/OAuth/Katana.vue'),
            meta: {
              auth: true,
              name: 'Katana - Integrations (Partner Integrations) - Settings',
            },
          },
          {
            path: 'oauth-quickbooks',
            name: 'config-int-quickbooks',
            component: () => import('../views/Settings/OAuth/QuickBooks.vue'),
            meta: {
              auth: true,
              name: 'Quickbook - Integrations (Partner Integrations) - Settings',
            },
          },
          {
            path: 'oauth-xero',
            name: 'config-int-xero',
            component: () => import('../views/Settings/OAuth/Xero.vue'),
            meta: {
              auth: true,
              name: 'Xero - Integrations (Partner Integrations) - Settings',
            },
          },
        ],
      },
      {
        path: 'api',
        name: 'config-api',
        // @ts-expect-error no types
        component: () => import('../views/Settings/SettingsAPI.vue'),
        meta: {
          auth: true,
          name: 'API - Settings',
        },
      },
      {
        // todo remove after 90 days of deployment
        path: 'frontend',
        name: 'config-frontend',
        redirect: redirectWithParams('integrations-widgets'),
        children: [
          {
            path: '',
            name: 'config-frontend-redirect',
            redirect: redirectWithParams('integrations-widgets'),
          },
          {
            path: 'core',
            name: 'integrations-widgets-core',
            redirect: redirectWithParams('config-frontend-core'),
            meta: {
              auth: true,
              name: 'Widgets (Core script) - Settings',
            },
          },
          {
            path: 'pdp',
            name: 'integrations-widgets-pdp',
            redirect: redirectWithParams('config-frontend-pdp'),
          },
          {
            path: 'css',
            name: 'integrations-widgets-css',
            redirect: redirectWithParams('config-frontend-css'),
          },
          {
            path: 'tips',
            name: 'integrations-widgets-tips',
            redirect: redirectWithParams('config-frontend-tips'),
          },
        ],
      },
      {
        path: 'shipping',
        name: 'config-shipping',
        // @ts-expect-error no types
        component: () => import('../views/Settings/SettingsShippingRates.vue'),
        meta: {
          auth: true,
          name: 'Shipping - Settings',
        },
      },
      {
        path: 'settings',
        name: 'config-settings',
        // @ts-expect-error no types
        component: () => import('../views/Settings/SettingsConfigurations.vue'),
        meta: { auth: true },
        children: [
          {
            path: '',
            redirect: redirectWithParams('config-settings-general'),
            name: 'config-settings-redirect',
          },
          {
            path: 'general',
            name: 'config-settings-general',
            // @ts-expect-error no types
            component: () => import('../views/Settings/SettingsConfigurationsGeneral.vue'),
            meta: {
              auth: true,
              name: 'Configurations - Settings',
            },
          },
        ],
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/v2/configuration',
    name: 'v2-configuration',
    component: () => import('../views/Settings/MainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: 'shipping',
        name: 'v2-config-shipping',
        component: () => import('../views/Settings/SettingsShippingMethodsV2.vue'),
        meta: {
          auth: true,
          name: 'Shipping - Settings',
        },
      },
      {
        path: 'shipping/create',
        name: 'v2-config-shipping-create',
        component: () => import('../views/Settings/SettingsShippingMethodCreateV2.vue'),
        meta: {
          auth: true,
          name: 'Create Shipping Method - Settings',
        },
      },
      {
        path: 'shipping/edit/:id',
        name: 'v2-config-shipping-edit',
        component: () => import('../views/Settings/SettingsShippingMethodEditV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Shipping Method - Settings',
        },
      },
      {
        path: 'api',
        name: 'v2-config-api',
        component: () => import('../views/Settings/SettingsAPIV2.vue'),
        meta: {
          auth: true,
          name: 'API - Settings',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/storefront',
    name: 'storefront',
    redirect: redirectWithParams('integrations-widgets'),
    meta: { auth: true },
  },
  {
    path: '/:siteId/:siteEnv/storefront/widgets',
    name: 'integrations-widgets',
    redirect: redirectWithParams('config-frontend-core'),
    meta: { auth: true },
  },
  {
    path: '/:siteId/:siteEnv/storefront/widgets/core',
    name: 'config-frontend-core',
    component: () => import('../views/Storefront/Widgets.vue'),
    meta: {
      auth: true,
      name: 'Widgets (Core script) - Storefront',
    },
  },
  {
    path: '/:siteId/:siteEnv/storefront/widgets/pdp',
    name: 'config-frontend-pdp',
    component: () => import('../views/Storefront/Widgets.vue'),
    meta: {
      auth: true,
      name: 'Widgets (Product page script) - Storefront',
    },
  },
  {
    path: '/:siteId/:siteEnv/storefront/widgets/css',
    name: 'config-frontend-css',
    component: () => import('../views/Storefront/Widgets.vue'),
    meta: {
      auth: true,
      name: 'Widgets (Custom design) - Storefront',
    },
  },
  {
    path: '/:siteId/:siteEnv/storefront/widgets/tips',
    name: 'config-frontend-tips',
    component: () => import('../views/Storefront/Widgets.vue'),
    meta: {
      auth: true,
      name: 'Widgets (Best-practice tips) - Storefront',
    },
  },
  {
    path: '/:siteId/:siteEnv/configuration/theme',
    name: 'config-theme',
    component: () => import('../views/Settings/SettingsTheme.vue'),
    meta: {
      auth: true,
      name: 'Theme - Settings',
    },
  },
  {
    path: '/:siteId/:siteEnv/configuration/theme/editor/:themeId/:slug?',
    name: 'theme-editor',
    component: () => import('../views/Settings/SettingsThemeEditor.vue'),
    meta: {
      auth: true,
      name: 'Theme Editor',
    },
  },
  {
    path: '/:siteId/settings',
    name: 'settings',
    component: () => import('../views/Account/SettingsV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: redirectWithParams('settings-account'),
        name: 'settings-redirect',
      },
      {
        path: 'account',
        name: 'settings-account',
        // @ts-expect-error no types
        component: () => import('../views/Account/SettingsAccount.vue'),
        meta: {
          auth: true,
          name: 'Account - Settings',
        },
      },
      {
        path: 'password',
        name: 'settings-password',
        // @ts-expect-error no types
        component: () => import('../views/Account/SettingsPassword.vue'),
        meta: {
          auth: true,
          name: 'Password - Settings',
        },
      },
      {
        path: 'plan',
        name: 'settings-plan',
        component: () => import('../views/Account/SettingsBillingPlan.vue'),
        meta: {
          auth: true,
          name: 'Plan - Settings',
        },
      },
      {
        path: 'team',
        name: 'settings-team',
        // @ts-expect-error no types
        component: () => import('../views/Account/SettingsTeam.vue'),
        meta: {
          auth: true,
          name: 'Team - Settings',
        },
      },
    ],
  },
  {
    path: '/:siteId/v2/settings/team',
    name: 'v2-team',
    component: () => import('../views/Account/SettingsV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        name: 'v2-settings-team',
        component: () => import('../views/Account/SettingsTeamV2.vue'),
        meta: {
          auth: true,
          name: 'Team - Settings',
        },
      },
      {
        path: 'invite',
        name: 'v2-settings-team-invite',
        component: () => import('../views/Account/SettingsTeamInviteV2.vue'),
        meta: {
          auth: true,
          name: 'Invite Team Member - Settings',
        },
      },
    ],
  },
  {
    path: '/:siteId/v2/settings',
    name: 'v2-settings',
    component: () => import('../views/Account/SettingsV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: 'password',
        name: 'v2-settings-password',
        component: () => import('../views/Account/SettingsPasswordV2.vue'),
        meta: {
          auth: true,
          name: 'Password - Settings',
        },
      },
    ],
  },
  {
    path: '/:siteId/staff',
    name: 'staff',
    // @ts-expect-error no types
    component: () => import('../views/Staff/Staff.vue'),
    meta: { auth: true, staffOnly: true },
    children: [
      {
        path: '',
        redirect: redirectWithParams('staff-manage-billing'),
        name: 'staff-redirect',
      },
      {
        path: 'manage-billing',
        name: 'staff-manage-billing',
        component: () => import('../views/Staff/Billing/ManageBillingV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Manage Billing',
        },
      },
      {
        path: 'manage-billing/:actionType',
        name: 'staff-manage-billing-action',
        component: () => import('../views/Staff/Billing/ViewActionV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Manage Billing',
        },
      },
      {
        path: 'manage-team',
        name: 'staff-manage-team',
        component: () => import('../views/Staff/Team/ManageTeamV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Manage Team',
        },
      },
      {
        path: 'manage-team/:actionType',
        name: 'staff-manage-team-action',
        component: () => import('../views/Staff/Team/ViewActionV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Manage Team',
        },
      },
      {
        path: 'customer-sync',
        name: 'staff-customer-sync',
        component: () => import('../views/Staff/StaffCustomerSyncV2.vue'),
        meta: {
          auth: true,
          name: 'Staff - Customer Sync',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/pricing',
    name: 'products',
    component: () => import('../views/Prices/Main.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: redirectWithParams('pricing-lists'),
        name: 'products-redirect',
      },
      {
        path: 'editor/:variantId?',
        name: 'pricing-editor',
        // @ts-expect-error no types
        component: () => import('../views/Prices/PriceEditor.vue'),
        meta: {
          auth: true,
          name: 'Price Editor',
        },
      },
      {
        path: 'discounts',
        name: 'pricing-discounts',
        // @ts-expect-error no types
        component: () => import('@/views/Prices/Discounts/List.vue'),
        meta: {
          auth: true,
          name: 'Discounts',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/pricing',
    component: () => import('../views/Prices/MainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: 'lists',
        name: 'pricing-lists',
        component: () => import('../views/Prices/PriceListsV2.vue'),
        meta: {
          auth: true,
          name: 'Price Lists',
        },
      },
      {
        path: 'lists/create',
        name: 'price-list-create',
        component: () => import('../views/Prices/PriceListCreateV2.vue'),
        meta: {
          auth: true,
          name: 'Price List Create',
        },
      },
      {
        path: 'lists/edit/:slug',
        name: 'price-list-edit',
        component: () => import('../views/Prices/PriceListEditV2.vue'),
        meta: {
          auth: true,
          name: 'Price List Edit',
        },
      },
    ],
  },
  {
    // todo: remove after 90 days
    path: '/:siteId/:siteEnv/data-sync',
    name: 'data-sync',
    redirect: redirectWithParams('integrations-products-sync'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: redirectWithParams('integrations-products-sync'),
        name: 'data-sync-redirect',
      },
      {
        path: 'products/:variantId?',
        name: 'sync-products',
        redirect: redirectWithParams('integrations-products-sync'),
      },
      {
        path: 'customers',
        name: 'sync-customers',
        redirect: redirectWithParams('integrations-customers-sync'),
      },
    ],
  },
  {
    // Legacy Orders URL
    path: '/:siteId/:siteEnv/orders',
    name: 'orders',
    redirect: redirectWithParams('activity'),
  },
  {
    // Legacy Discounts URL
    path: '/:siteId/:siteEnv/discounts',
    name: 'discounts',
    redirect: redirectWithParams('pricing-discounts'),
  },
  {
    path: '/:siteId/:siteEnv/customers',
    name: 'customers',
    component: () => import('../views/Customers/MainV2.vue'),
    meta: {
      auth: true,
    },
    children: [
      {
        path: '',
        redirect: redirectWithParams('customers-groups'),
        name: 'customers-redirect',
      },
      {
        path: 'groups',
        name: 'customers-groups',
        component: () => import('../views/Customers/CustomerGroupsV2.vue'),
        meta: {
          auth: true,
          name: 'Customer Groups',
        },
      },
      {
        path: 'groups/create',
        name: 'customers-groups-create',
        component: () => import('../views/Customers/CustomerGroupsBuilderV2.vue'),
        meta: {
          auth: true,
          name: 'Create Customer Group',
        },
      },
      {
        path: 'groups/edit/:slug',
        name: 'customers-groups-edit',
        component: () => import('../views/Customers/CustomerGroupsBuilderV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Customer Group',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/v2/customers',
    name: 'v2-customers',
    component: () => import('../views/Customers/MainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: redirectWithParams('v2-customers-groups'),
        name: 'v2-customers-redirect',
      },
      {
        path: 'groups',
        name: 'v2-customers-groups',
        component: () => import('../views/Customers/CustomerGroupsV2.vue'),
        meta: {
          auth: true,
          name: 'Customer Groups - Customer',
        },
      },
      {
        path: 'groups/create',
        name: 'v2-customers-groups-create',
        component: () => import('../views/Customers/CustomerGroupsBuilderV2.vue'),
        meta: {
          auth: true,
          name: 'Create Customer Group',
        },
      },
      {
        path: 'groups/edit/:slug',
        name: 'v2-customers-groups-edit',
        component: () => import('../views/Customers/CustomerGroupsBuilderV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Customer Group',
        },
      },
      {
        path: 'list',
        name: 'v2-customer-list',
        component: () => import('../views/Customers/CustomersCompanyUsersV2.vue'),
        meta: {
          auth: true,
          name: 'Customers',
        },
      },
      {
        path: 'companies',
        name: 'v2-customers-companies',
        component: () => import('../views/Customers/CompaniesV2.vue'),
        meta: {
          auth: true,
          name: 'Companies',
        },
      },
      {
        path: 'companies/create',
        name: 'v2-customers-companies-create',
        component: () => import('../views/Customers/CompanyFormV2.vue'),
        meta: {
          auth: true,
          name: 'Create Company',
        },
      },
      {
        path: 'companies/edit/:id',
        name: 'v2-customers-companies-edit',
        component: () => import('../views/Customers/CompanyFormV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Company',
        },
      },
      {
        path: 'agents',
        name: 'v2-sales-agents',
        component: () => import('../views/Customers/CustomerSalesAgentsV2.vue'),
        meta: {
          auth: true,
          name: 'Sales Agents',
        },
      },
      {
        path: 'list/details/:id',
        name: 'v2-customers-details',
        component: () => import('../views/Customers/CustomerDetailsV2.vue'),
        meta: {
          auth: true,
          name: 'Customer Details',
        },
      },
      {
        path: 'agents/details/:id',
        name: 'v2-agents-details',
        component: () => import('../views/Customers/CustomerDetailsV2.vue'),
        meta: {
          auth: true,
          name: 'Sales Agents Details',
        },
      },
      {
        path: 'roles',
        name: 'v2-customers-roles',
        component: () => import('../views/Customers/CustomerRolesV2.vue'),
        meta: {
          auth: true,
          name: 'Roles',
        },
      },
      {
        path: 'roles/create',
        name: 'v2-customers-roles-create',
        component: () => import('../views/Customers/CustomerRoleFormV2.vue'),
        meta: {
          auth: true,
          name: 'Create Role',
        },
      },
      {
        path: 'roles/edit/:slug',
        name: 'v2-customers-roles-edit',
        component: () => import('../views/Customers/CustomerRoleFormV2.vue'),
        meta: {
          auth: true,
          name: 'Edit Role',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/integrations',
    name: 'integrations',
    // @ts-expect-error no types
    component: () => import('../views/Integrations/MainLayout.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: redirectWithParams('integrations-platform'),
        name: 'integrations-redirect',
      },
      {
        path: 'platform',
        name: 'integrations-platform',
        // @ts-expect-error no types
        component: () => import('../views/Integrations/Platform.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Platform',
        },
      },
      {
        path: 'partners',
        name: 'integrations-partners',
        // @ts-expect-error no types
        component: () => import('../views/Integrations/Partners.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Partners',
        },
      },
      {
        path: 'partners/oauth',
        name: 'integration-partners-oauth',
        meta: {
          auth: true,
          name: 'Integrations - Partners OAuth',
        },
        children: [
          {
            path: 'brightpearl',
            name: 'int-brightpearl-oauth',
            component: () => import('../views/Settings/OAuth/Brightpearl.vue'),
            meta: {
              auth: true,
              name: 'Brightpearl - Integrations (Partner Integrations) - Settings',
            },
          },
          {
            path: 'katana',
            name: 'int-katana-oauth',
            component: () => import('../views/Settings/OAuth/Katana.vue'),
            meta: {
              auth: true,
              name: 'Katana - Integrations (Partner Integrations) - Settings',
            },
          },
          {
            path: 'quickbooks',
            name: 'int-quickbooks-oauth',
            component: () => import('../views/Settings/OAuth/QuickBooks.vue'),
            meta: {
              auth: true,
              name: 'Quickbook - Integrations (Partner Integrations)',
            },
          },
          {
            path: 'xero',
            name: 'int-xero-oauth',
            component: () => import('../views/Settings/OAuth/Xero.vue'),
            meta: {
              auth: true,
              name: 'Xero - Integrations (Partner Integrations) - Settings',
            },
          },
        ],
      },
      {
        path: 'customers-sync',
        name: 'integrations-customers-sync',
        // @ts-expect-error no types
        component: () => import('../views/Integrations/CustomersSync.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Customers Sync',
          redirectName: 'v2-customer-list',
          redirectFeature: features.CUSTOMERS_V2,
        },
      },
      {
        path: 'products-sync',
        name: 'integrations-products-sync',
        component: () => import('../views/Integrations/ProductsSync.vue'),
        meta: {
          auth: true,
          name: 'Integrations - Products Sync',
        },
      },
      {
        path: 'widgets',
        name: 'integrations-widgets-legacy',
        redirect: redirectWithParams('integrations-widgets'),
        meta: { auth: true },
        children: [
          {
            path: 'core',
            name: 'config-frontend-core-legacy',
            redirect: redirectWithParams('config-frontend-core'),
          },
          {
            path: 'pdp',
            name: 'config-frontend-pdp-legacy',
            redirect: redirectWithParams('config-frontend-pdp'),
          },
          {
            path: 'css',
            name: 'config-frontend-css-legacy',
            redirect: redirectWithParams('config-frontend-css'),
          },
          {
            path: 'tips',
            name: 'config-frontend-tips-legacy',
            redirect: redirectWithParams('config-frontend-tips'),
          },
        ],
      },
    ],
  },

  {
    path: '/:siteId/:siteEnv/analytics',
    name: 'analytics',
    component: () => import('../views/Analytics/MainV2.vue'),
    meta: { auth: true },
    children: [
      {
        path: '',
        redirect: redirectWithParams('analytics-dash'),
        name: 'analytics-redirect',
      },
      {
        path: 'dashboard',
        name: 'analytics-dash',
        component: () => import('../views/Analytics/AnalyticsDashboard.vue'),
        meta: {
          auth: true,
          name: 'Analytics',
        },
      },
      {
        path: 'chat',
        name: 'analytics-chat',
        component: () => import('../views/Analytics/AnalyticsChat.vue'),
        meta: {
          auth: true,
          name: 'Analytics Chat',
        },
      },
      {
        path: 'report/:reportSlug',
        name: 'analytics-report',
        component: () => import('../views/Analytics/AnalyticsReport.vue'),
        meta: {
          auth: true,
          name: 'Analytics Report',
        },
      },
      {
        path: 'custom/:customId',
        name: 'analytics-custom',
        component: () => import('../views/Analytics/AnalyticsCustomReport.vue'),
        meta: {
          auth: true,
          name: 'Analytics Custom Report',
        },
      },
    ],
  },
  {
    path: '/:siteId/:siteEnv/sales-agents',
    name: 'sales-agents',
    // @ts-expect-error no types
    component: () => import('../views/SalesAgents/SalesAgents.vue'),
    meta: {
      auth: true,
      name: 'Sales Agents',
      redirectName: 'v2-sales-agents',
      redirectFeature: features.CUSTOMERS_V2,
    },
  },
  {
    path: '/help',
    name: 'help',
    // @ts-expect-error no types
    component: () => import('../views/Help.vue'),
    meta: {
      auth: true,
      name: 'Help',
    },
  },
  {
    path: '/v2/help',
    name: 'help_v2',
    component: () => import('../views/HelpV2.vue'),
    meta: {
      auth: true,
      name: 'Help',
    },
  },
  {
    path: '/partner-register',
    name: 'register-partner',
    // @ts-expect-error no types
    component: () => import('../views/Partners/Register.vue'),
    meta: {
      auth: false,
      name: 'Partner Register',
    },
  },
  {
    path: '/register-team-member',
    name: 'register-team-member',
    // @ts-expect-error no types
    component: () => import('../views/RegisterTeamMember.vue'),
    meta: {
      auth: false,
      name: 'Register Team Member',
    },
  },
  {
    path: '/login',
    name: 'login',
    // @ts-expect-error no types
    component: () => import('../views/Login.vue'),
    meta: {
      auth: false,
      name: 'Login',
    },
  },
  {
    path: '/logout',
    name: 'logout',
    // @ts-expect-error no types
    component: () => import('../views/Logout.vue'),
    meta: {
      auth: true,
      name: 'Logout',
    },
  },
  {
    path: '/register',
    name: 'register',
    // @ts-expect-error no types
    component: () => import('../views/Register.vue'),
    meta: {
      auth: false,
      name: 'Register',
    },
  },
  {
    path: '/go-register',
    name: 'register-sales',
    redirect: (to) => {
      return {
        path: '/register',
        query: {
          ...to.query,
          app_registration_source: 'sales_pipeline_tracked_link',
        },
      };
    },
    meta: {
      auth: false,
      name: 'Register - Sales Pipeline Redirect',
    },
  },
  {
    path: '/go-enterprise',
    name: 'register-enterprise',
    // @ts-expect-error no types
    component: () => import('../views/RegisterEnterprise.vue'),
    meta: {
      auth: false,
      name: 'Register - Enterprise',
    },
  },
  {
    path: '/register-invite',
    name: 'register-invite',
    // @ts-expect-error no types
    component: () => import('../views/RegisterInvite.vue'),
    meta: {
      auth: false,
      name: 'Invite to Register',
    },
  },
  {
    path: '/no-team',
    name: 'no-team',
    // @ts-expect-error no types
    component: () => import('../views/NoTeam.vue'),
    meta: {
      auth: true,
      name: 'No Team',
    },
  },
  {
    path: '/forgotten-password',
    name: 'forgotten-password',
    // @ts-expect-error no types
    component: () => import('../views/ForgottenPassword.vue'),
    meta: {
      auth: false,
      name: 'Forgotten Password',
    },
  },
  {
    path: '/reset-password/:token',
    name: 'reset-password',
    // @ts-expect-error no types
    component: () => import('../views/ResetPassword.vue'),
    meta: {
      auth: false,
      name: 'Reset Password',
    },
  },
  {
    path: '/switch-account/:teamId',
    name: 'switch-account',
    component: () => import('../views/SwitchAccount.vue'),
    meta: {
      auth: true,
      name: 'Switch Account',
    },
  },
];

// Below creates extra routes for any routes with following pre-fixes:
// - /:siteId/:siteEnv
// routes are setup to redirect correctly with siteId/siteEnv in the path
const routesWithSiteIdRedirect = new Map<string, Pick<RouteRecordRaw, 'path' | 'name'>>();

const joinRoutePath = (parentPath: string, childPath: string): string => {
  if (!childPath) {
    return parentPath;
  }

  if (childPath.startsWith('/')) {
    return childPath;
  }

  return `${parentPath}/${childPath}`.replace(/\/+/g, '/');
};

const collectRoutesWithSiteIdRedirect = (
  route: RouteRecordRaw,
  currentPath: string = route.path,
): void => {
  // empty-path index children collapse onto the parent path, so skip them entirely
  if (route.path !== '' && !routesWithSiteIdRedirect.has(currentPath)) {
    if (route.name) {
      routesWithSiteIdRedirect.set(currentPath, {
        path: currentPath,
        name: route.name,
      });
    } else {
      console.error(`All routes must be setup with a route name ${currentPath}`);
    }
  }

  route.children?.forEach((childRoute) => {
    collectRoutesWithSiteIdRedirect(childRoute, joinRoutePath(currentPath, childRoute.path));
  });
};

routes
  .filter((route) => route.path.startsWith('/:siteId/'))
  .forEach((route) => {
    collectRoutesWithSiteIdRedirect(route);
  });

routes.push(
  ...Array.from(routesWithSiteIdRedirect.values()).reduce<RouteRecordRaw[]>(
    (generatedRoutes, route) => {
      const path = route.path.replace('/:siteId', '').replace('/:siteEnv', '');
      if (!path) {
        return generatedRoutes;
      }

      generatedRoutes.push({
        path,
        component: () => import('../views/Redirect.vue'),
        meta: {
          // @ts-expect-error auth not expected here
          auth: true,
          _note: 'this is a generated route, see router/index.ts',
          redirectName: route.name,
        } satisfies RouteRecordRaw,
      });

      return generatedRoutes;
    },
    [],
  ),
);

// finally push the thankful 404
routes.push({
  path: '/:pathMatch(.*)*',
  name: 'four-oh-four',
  redirect: '/',
  meta: { auth: true, redirectName: 'home' },
});

// PR preview builds are served from a branch sub-path (e.g. /branch-name/) on a
// static bucket that has no per-branch SPA fallback, so a hard refresh on a deep
// link would fall back to the root index.html (base "/") and parse the branch name
// as a route. Hash routing keeps the server-visible path at the deploy base and
// resolves routes client-side, so previews work on refresh without server rewrites.
// Enabled only for preview builds via the CI workflow; master/prod keep clean URLs.
const history =
  import.meta.env.VITE_PREVIEW_HASH_ROUTING === 'true'
    ? createWebHashHistory(import.meta.env.BASE_URL)
    : createWebHistory(import.meta.env.BASE_URL);

const router = createRouter({
  history,
  routes,
});

router.beforeEach(async ({ meta, path, params, query }, _from, next) => {
  const envStore = useEnvStore();
  const authStore = useAuthStore();

  const { auth, redirectName, staffOnly, redirectFeature } = meta as {
    auth: boolean;
    redirectName: string;
    staffOnly: boolean;
    redirectFeature?: FeatureFlag;
  };

  await authStore.loadUser();

  const isLoggedIn = authStore.isLoggedIn;
  const currentTeam = authStore.data?.currentTeam ?? null;
  const hasTeam = currentTeam !== null;
  const isSparkStaff = authStore.isSparkStaff;

  // Redirect logged-in users away from logged out urls
  if (
    isLoggedIn &&
    (path === '/login' ||
      path === '/register' ||
      path === '/go-enterprise' ||
      path === '/partner-register')
  ) {
    next({
      name: 'home',
    });
    return;
  }

  // Pre-initial checks
  switch (true) {
    case !auth && !isLoggedIn:
      // If no auth is needed and no login is present continue with out any setup
      next();
      return;
    case auth && !isLoggedIn:
      // If auth is required and the user is not logged in, push to login
      next({
        name: 'login',
        query: { from: window.location.pathname },
      });
      return;
    case staffOnly && !isSparkStaff:
      next({
        name: 'home',
      });
      return;
    case path !== '/no-team' && path !== '/logout' && auth && !hasTeam:
      next({ name: 'no-team' });
      return;
  }

  // Load team / team environment
  // First from the routing params if provided
  if (params?.siteId) {
    // If siteEnv is not provided, default to test for partners, live for all others
    const siteEnv = params?.siteEnv
      ? (params.siteEnv as string)
      : path.includes('/partners')
        ? 'test'
        : 'live';

    await envStore.loadEnv({
      siteId: params.siteId as string,
      siteEnv,
    });
  } else if (!envStore.isLoaded && currentTeam) {
    // Otherwise let's load the default for the user logged in
    await envStore.loadEnv({
      siteId: currentTeam.slugged_name,
      siteEnv: currentTeam.is_partner ? 'test' : 'live',
    });
  }

  const isSiteReady = envStore.isSiteReady;
  if (path !== '/getting-ready' && !isSiteReady && hasTeam) {
    next({ name: 'Getting Ready' });
    return;
  }

  const siteID = envStore.env?.team.slugged_name as string;
  const platform = envStore.env?.platform as Platform;

  // Redirect route?
  if (redirectName) {
    if (!redirectFeature || featureEnabled(redirectFeature, siteID, platform)) {
      next({
        name: redirectName,
        params: {
          siteId: siteID,
          siteEnv: envStore.env?.environment as string,
        },
        query,
      });
      return;
    }
  }

  next();
});

router.afterEach(({ path, meta }) => {
  window.Intercom('update');
  window._hsq = window._hsq || [];
  const _hsq = window._hsq;
  _hsq.push(['setPath', path]);
  _hsq.push(['trackPageView']);

  // Use next tick to handle router history correctly
  // see: https://github.com/vuejs/vue-router/issues/914#issuecomment-384477609
  nextTick(() => {
    document.title = meta?.name ? `${meta.name} - ${DEFAULT_TITLE}` : DEFAULT_TITLE;
  });
});

export default router;
